import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, effect, inject, NgModule, OnInit, signal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { NgxsFormPluginModule } from '@ngxs/form-plugin';
import { NgxsModule, select } from '@ngxs/store';
import {
  AnimateModule,
  BasicPage,
  GtmClickDirective,
  InputValidatorDirective,
  InvalidControlScrollDirective,
  IPartnerCodesList,
  IS_DEVELOPMENT,
  ValidateMessageComponent,
  WwwRouteSlug,
} from '@pro-connect/shared';
import { NgxMaskDirective } from 'ngx-mask';
import { MyDataState, MyDataStateGetListOfPartnerCodes, MyDataStateGetPartnerCodeDetails } from './my-data.state';
import { ExpanderComponent } from './components/expander/expander.component';
import { AgreementsComponent } from './components/agreements/agreements.component';
import { PaymentHistoryComponent } from './components/payment-history/payment-history.component';
import { PartnersBenefitsComponent } from './components/partners-benefits/partners-benefits.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { firstValueFrom } from 'rxjs';
import { PartnerCodeDetailsModalComponent } from './components/partner-code-details-modal/partner-code-details-modal.component';
import { Angulartics2GoogleTagManager } from 'angulartics2';

export type MyDataFormType = {
  name: FormControl<string | null>;
  nip: FormControl<string | null>;
  street: FormControl<string | null>;
  zipCode: FormControl<string | null>;
  city: FormControl<string | null>;
  firstname: FormControl<string | null>;
  lastname: FormControl<string | null>;
  mobile: FormControl<string | null>;
  email: FormControl<string | null>;
};

@NgModule({
  imports: [NgxsModule.forFeature([MyDataState])],
})
class MyDataStateLazyModule {}

@Component({
  templateUrl: './my-data.component.html',
  styleUrls: ['./my-data.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    RouterModule,
    AnimateModule,
    ReactiveFormsModule,
    FormsModule,
    NgxsFormPluginModule,
    ValidateMessageComponent,
    InputValidatorDirective,
    MyDataStateLazyModule,
    InvalidControlScrollDirective,
    NgxMaskDirective,
    ExpanderComponent,
    AgreementsComponent,
    PaymentHistoryComponent,
    PartnersBenefitsComponent,
    GtmClickDirective,
  ],
})
export class MyDataComponent extends BasicPage implements OnInit {
  public override readonly loading = select(MyDataState.loading);
  public readonly partnerCodes = select(MyDataState.partnerCodes);
  public readonly partnerCodeDetails = select(MyDataState.partnerCodeDetails);
  public readonly WwwRouteSlug = WwwRouteSlug;
  public readonly formGroup: FormGroup<MyDataFormType>;
  public readonly initialExpanderOpen = signal(true);
  private readonly _angulartics2GoogleTagManager = inject(Angulartics2GoogleTagManager);

  constructor(
    private readonly _formBuilder: FormBuilder,
    private readonly _ngbModal: NgbModal,
  ) {
    super();
    this.formGroup = this._formBuilder.group({
      name: new FormControl<string | null>(null, [Validators.required]),
      nip: new FormControl<string | null>(null, [Validators.required]),
      street: new FormControl<string | null>(null, [Validators.required]),
      zipCode: new FormControl<string | null>(null, [Validators.required]),
      city: new FormControl<string | null>(null, [Validators.required]),
      firstname: new FormControl<string | null>(null, [Validators.required]),
      lastname: new FormControl<string | null>(null, [Validators.required]),
      mobile: new FormControl<string | null>(null, [Validators.required]),
      email: new FormControl<string | null>(null, [Validators.required]),
    });
    if (this.user()) {
      effect(() => {
        const me = this.user();
        this.formGroup.patchValue({
          firstname: me?.firstname ? me.firstname : '-',
          lastname: me?.lastname ? me.lastname : '-',
          name: me?.name ? me.name : '-',
          city: me?.city ? me.city : '-',
          email: me?.email ? me.email : '-',
          mobile: me?.mobile ? me.mobile : '-',
          nip: me?.nip ? me.nip : '-',
          street: me?.street ? me.street : '-',
          zipCode: me?.zip_code ? me.zip_code : '-',
        });
        this.formGroup.disable();
        this.formGroup.updateValueAndValidity();
      });
    }
  }

  public ngOnInit(): void {
    this._store.dispatch(new MyDataStateGetListOfPartnerCodes());
    this._pushLayer();
  }

  get f() {
    return this.formGroup.controls;
  }

  public async openPartnerCodeDetailsModal(partnerCodesList: IPartnerCodesList): Promise<void> {
    const id = partnerCodesList.id;
    await firstValueFrom(this._store.dispatch(new MyDataStateGetPartnerCodeDetails({ id })));

    const ngbModalRef = this._ngbModal.open(PartnerCodeDetailsModalComponent, {
      centered: true,
      keyboard: true,
      backdrop: true,
      modalDialogClass: 'partner-code-modal-container',
    });
    ngbModalRef.componentInstance.codeDetails = signal(this.partnerCodeDetails()[id]);
  }

  private _pushLayer(): void {
    this._angulartics2GoogleTagManager.pushLayer({
      brand: 'LDB',
      language: 'pl',
      country: 'Pl',
      siteTypeLevel: this._isDevelopment ? 'test' : 'main',
      pageCategory: 'my account',
      geoIPLocation: 'Pl',
      uiLoggedStatus: !!this.user() ? 'logged' : 'not logged',
      uiType: this.user()?.type,
    });
  }
}
